import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { APP_FEATURE_LABELS } from 'modules/organizer/domain/constants/app-feature-labels';
import { APP_FEATURES, appFeature } from 'modules/organizer/domain/interfaces/available-feature';
import { formatRublesFromMinorUnits } from 'modules/organizer/domain/utils/money';
import { formatSubscriptionPeriod } from 'modules/organizer/domain/utils/subscription-period';
import { useGetTournamentPlansQuery, useSetTournamentPlanFeaturesMutation } from 'modules/organizer/store/billing-api';
import { diffPlanFeatures, featureKeys, sameFeatureSet } from 'modules/plans/domain/diff-plan-features';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { msg, ph } from '@lingui/core/macro';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SyncLoader } from 'react-spinners';
import type { MessageDescriptor } from 'shared/i18n';
import UDCheckbox from 'ud-form/components/checkbox/component';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';

function formatFeatureList(features: appFeature[], translate: (descriptor: MessageDescriptor) => string): string {
  return features.map((feature) => translate(APP_FEATURE_LABELS[feature])).join(', ');
}

const PlansDetailPage: React.FC = () => {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const planId = Number(id);
  const { setHeaderContent } = useMainLayout();
  const { data: plans, isLoading, isError } = useGetTournamentPlansQuery();
  const [setFeatures, { isLoading: isSaving }] = useSetTournamentPlanFeaturesMutation();

  const plan = useMemo(() => plans?.find((item) => item.id === planId), [plans, planId]);
  const currentFeatures = useMemo(() => (plan ? featureKeys(plan.features) : []), [plan]);
  const currentKey = currentFeatures.join(',');
  const [draft, setDraft] = useState<appFeature[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    setDraft(currentKey === '' ? [] : (currentKey.split(',') as appFeature[]));
  }, [currentKey]);

  const breadcrumbs = useMemo(
    () => [
      { label: t`Тарифы`, to: Pages.PLANS.INDEX },
      { label: plan?.name ?? t`Тарифы`, to: Pages.PLANS.builders.view(planId) },
    ],
    [plan?.name, planId, t],
  );

  useEffect(() => {
    setHeaderContent(<UDBreadcrumbs breadcrumbs={breadcrumbs} />);
    return () => setHeaderContent(null);
  }, [breadcrumbs, setHeaderContent]);

  const isDirty = !sameFeatureSet(currentFeatures, draft);
  const diff = useMemo(() => diffPlanFeatures(currentFeatures, draft), [currentFeatures, draft]);

  const toggleFeature = useCallback((feature: appFeature) => {
    setDraft((prev) => (prev.includes(feature) ? prev.filter((item) => item !== feature) : [...prev, feature]));
  }, []);

  const confirmDescription = useMemo(() => {
    const lines = [t`Все организации с этим тарифом сразу получат новый набор фич.`];
    if (diff.add.length) {
      const features = formatFeatureList(diff.add, t);
      lines.push(t`Добавить: ${features}`);
    }
    if (diff.remove.length) {
      const features = formatFeatureList(diff.remove, t);
      lines.push(t`Убрать: ${features}`);
    }
    return lines.join('\n\n');
  }, [diff.add, diff.remove, t]);

  const handleSave = useCallback(async () => {
    try {
      await setFeatures({ planId, features: draft }).unwrap();
      setConfirmOpen(false);
      toast.success(t`Фичи тарифа сохранены`);
    } catch (error) {
      toastApiError(error, msg`Не удалось сохранить фичи тарифа`);
    }
  }, [draft, planId, setFeatures, t, toastApiError]);

  if (isLoading) {
    return (
      <div className="flex-center w-full px-6" style={{ height: '100%', minHeight: 'calc(100vh - 241px)' }}>
        <SyncLoader />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex-center w-full px-6 py-10">
        <UDText type="callout" className="text-surfaceRomanSilver30">
          {t`Ошибка загрузки данных. Попробуйте обновить страницу.`}
        </UDText>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="flex-center w-full flex-col gap-4 px-6 py-10">
        <UDText type="callout" className="text-surfaceRomanSilver30">
          {t`Тариф не найден`}
        </UDText>
        <UDButton variant="secondary" onClick={() => navigate(Pages.PLANS.INDEX)}>
          {t`К списку тарифов`}
        </UDButton>
      </div>
    );
  }

  return (
    <div className="w-full px-6 py-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <UDText type="headline">{plan.name}</UDText>
          <UDText type="subhead" className="text-surfaceRomanSilver30 mt-1">
            {formatRublesFromMinorUnits(plan.priceInMinorUnits)} ₽ / {formatSubscriptionPeriod(plan.period)}
          </UDText>
        </div>
        <div className="flex gap-3">
          <UDButton variant="secondary" onClick={() => navigate(Pages.PLANS.INDEX)}>
            {t`К списку тарифов`}
          </UDButton>
          <UDButton variant="primary" disabled={!isDirty} loading={isSaving} onClick={() => setConfirmOpen(true)}>
            {t`Сохранить`}
          </UDButton>
        </div>
      </div>

      <div className="flex flex-col gap-1 rounded-lg bg-white px-5 py-3">
        {APP_FEATURES.map((feature) => {
          const checked = draft.includes(feature);
          const label = t(APP_FEATURE_LABELS[feature]);
          return (
            <div
              key={feature}
              data-testid={`plan-feature-${feature}`}
              className="flex cursor-pointer items-center gap-3 py-2"
              onClick={() => toggleFeature(feature)}
            >
              <UDCheckbox checked={checked} readOnly aria-label={label} onClick={(event) => event.stopPropagation()} />
              <UDText type="subhead">{label}</UDText>
            </div>
          );
        })}
      </div>

      <Modal.Confirm
        show={confirmOpen}
        title={t`Изменить фичи тарифа «${ph({ name: plan.name })}»?`}
        description={confirmDescription}
        confirmText={t`Сохранить`}
        tone="neutral"
        isLoading={isSaving}
        onConfirm={handleSave}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default PlansDetailPage;
